import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GRADE_COLOR = {
  'A':  'bg-emerald-100 text-emerald-700',
  'A-': 'bg-emerald-50  text-emerald-600',
  'B+': 'bg-blue-100    text-blue-700',
  'B-': 'bg-blue-50     text-blue-600',
  'C+': 'bg-amber-100   text-amber-700',
  'C':  'bg-amber-50    text-amber-600',
};
const gradeClass = g => GRADE_COLOR[g] || 'bg-gray-100 text-gray-600';

export default function SantriUjianTahfidz() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [santriProfile, setSantriProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.santri_id) { setLoading(false); return; }
    Promise.all([
      axios.get(`${API}/ujian-tahfidz`, { withCredentials: true }),
      axios.get(`${API}/santri/${user.santri_id}`),
    ]).then(([r, s]) => {
      setRecords(r.data || []);
      setSantriProfile(s.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 pb-28 md:pb-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-950 to-emerald-800 px-4 pt-12 pb-6">
          <div className="flex items-center gap-3 mb-3">
            <button data-testid="santri-ujian-back-btn" onClick={() => navigate('/santri')}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
              <ArrowLeft size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-white font-bold text-lg">Ujian Tahfidz</h1>
              <p className="text-emerald-300 text-xs">Hasil penilaian ujian hafalan</p>
            </div>
          </div>
          {santriProfile && (
            <div data-testid="santri-ujian-profile" className="bg-white/10 rounded-2xl px-4 py-3 mt-2">
              <p className="text-white font-semibold">{santriProfile.nama}</p>
              <p className="text-emerald-300 text-xs">
                NIS: {santriProfile.nis || '-'} · Kelas: {santriProfile.kelas || '-'}
              </p>
            </div>
          )}
        </div>

        <div className="px-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : records.length === 0 ? (
            <div data-testid="ujian-empty-state" className="text-center py-16 text-gray-400">
              <BookOpen size={44} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm font-semibold text-gray-500">Belum ada data ujian tahfidz</p>
              <p className="text-xs mt-1 text-gray-400">Data akan muncul setelah guru menginput hasil ujian</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="ujian-records-list">
              {records.map((rec, idx) => (
                <div key={idx} data-testid={`ujian-record-${idx}`}
                  className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {/* Semester header */}
                  <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
                    <p className="font-semibold text-emerald-800 text-sm">{rec.semester}</p>
                  </div>

                  {/* Tajwid */}
                  {rec.tajwid_nilai && (
                    <div data-testid={`ujian-tajwid-${idx}`} className="px-4 py-3 border-b border-gray-50">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Tajwid</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Penilaian Tajwid</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${gradeClass(rec.tajwid_nilai)}`}>
                          {rec.tajwid_nilai}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* MHQ */}
                  {rec.mhq?.length > 0 && (
                    <div data-testid={`ujian-mhq-${idx}`} className="px-4 py-3 border-b border-gray-50">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">MHQ</p>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 border-b border-gray-100">
                            <th className="text-left py-1 font-medium">Juz</th>
                            <th className="text-right py-1 font-medium">Nilai</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rec.mhq.map((m, i) => (
                            <tr key={i} className="border-b border-gray-50 last:border-0">
                              <td className="py-1.5 text-gray-700">Juz {m.juz}</td>
                              <td className="py-1.5 text-right">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${gradeClass(m.nilai)}`}>
                                  {m.nilai}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Murojaah */}
                  {rec.murojaah?.length > 0 && (
                    <div data-testid={`ujian-murojaah-${idx}`} className="px-4 py-3">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Murojaah</p>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 border-b border-gray-100">
                            <th className="text-left py-1 font-medium">Juz</th>
                            <th className="text-left py-1 font-medium">Setoran</th>
                            <th className="text-right py-1 font-medium">Nilai</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rec.murojaah.map((m, i) => (
                            <tr key={i} className="border-b border-gray-50 last:border-0">
                              <td className="py-1.5 text-gray-700">Juz {m.juz}</td>
                              <td className="py-1.5 text-gray-500">{m.setoran}</td>
                              <td className="py-1.5 text-right">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${gradeClass(m.nilai)}`}>
                                  {m.nilai}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
